"use strict";

// Downloads the server-built inspection starting data for an asset type into its own resource,
// so new inspections can start offline from the same data the online form uses.
const InspectableAssetCache = function () {
  // Catch basins and sBMPs share the bmpFcs asset type but have separate map layers, so each
  // layer's cache downloads only the subset of assets that layer lists.
  const CACHES_BY_RESOURCE_KEY = {
    manholeInspectable: { assetType: "manhole", subset: null },
    pipeInspectable: { assetType: "pipe", subset: null },
    culvertInspectable: { assetType: "culvert", subset: null },
    openChannelInspectable: { assetType: "openChannel", subset: null },
    liftStationInspectable: { assetType: "liftStation", subset: null },
    cleanoutInspectable: { assetType: "cleanout", subset: null },
    sbmpInspectable: { assetType: "bmpFcs", subset: "sbmps" },
  };
  const downloads = {};

  const hasCacheForAssetType = (assetType) => _getResourceKeys(assetType).length > 0;

  // DataResource swallows API errors, so the group is only recorded once the download succeeds.
  // It's cleared before every download, so a failed refresh is retried by the next start.
  const fetchAll = async function (resourceKey) {
    const { assetType, subset } = CACHES_BY_RESOURCE_KEY[resourceKey];
    const groupId = Session.getGroupIdToPass();
    const rows = await ApiCalls.getMaintenanceInspectableAssets(assetType, subset);

    _getDownload(resourceKey).downloadedGroupId = groupId;
    return rows.map(_toCachedRow);
  };

  // Downloads are queued because DataResource joins a fetch already in flight instead of
  // starting one, which would drop the download for a group switched to mid-download.
  // Starts without waiting, so the caller isn't delayed. Pass refreshData when the server data
  // may have changed (e.g. after an inspection or inventory save), otherwise a group that's
  // already downloaded is skipped.
  const startDownload = function (resourceKey, { refreshData = false } = {}) {
    _queueCacheChange(resourceKey, (resource, download) =>
      _downloadIfStale(resource, download, refreshData),
    );
  };

  const _downloadIfStale = async function (resource, download, refreshData) {
    if (!refreshData && download.downloadedGroupId === Session.getGroupIdToPass()) {
      return;
    }

    download.downloadedGroupId = null;
    await resource.getAll({ refreshData: true, loadingScreen: false });
  };

  // Call after the client changes an asset's inspection data on the server. Only assets that are
  // already cached are updated, the rest arrive with the next full download.
  const refreshAsset = function (assetType, assetId) {
    const id = _toCacheKey(assetId);

    for (const resourceKey of _getResourceKeys(assetType)) {
      _queueCacheChange(resourceKey, async (resource, download) => {
        if (!(await resource.getOfflineById(id))) {
          return;
        }

        try {
          const row = await ApiCalls.getMaintenanceInspectableAsset(id, assetType, {
            inBackground: true,
          });
          await resource.saveOffline(_toCachedRow(row));
        } catch (e) {
          ApiError.assertIs(e);
          await _handleFailedAssetRefresh(e, resource, download, id);
        }
      });
    }
  };

  // The asset is gone or no longer inspectable by this user, otherwise the row is stale until
  // the next full download.
  const _handleFailedAssetRefresh = async function (e, resource, download, id) {
    if ([403, 404].includes(e.status)) {
      await resource.deleteOffline(id);
    } else {
      download.downloadedGroupId = null;
    }
  };

  const removeAsset = function (assetType, assetId) {
    for (const resourceKey of _getResourceKeys(assetType)) {
      _queueCacheChange(resourceKey, (resource) => resource.deleteOffline(_toCacheKey(assetId)));
    }
  };

  // Rows are keyed by numeric id, but ids read from routes (e.g. fact sheets) are strings
  const _toCacheKey = (assetId) => Number(assetId);

  const _getResourceKeys = (assetType) =>
    Object.keys(CACHES_BY_RESOURCE_KEY).filter(
      (resourceKey) => CACHES_BY_RESOURCE_KEY[resourceKey].assetType === assetType,
    );

  // Every change shares the cache's queue, so a full download that started before a change
  // can't overwrite it with older data.
  const _queueCacheChange = function (resourceKey, change) {
    const { assetType } = CACHES_BY_RESOURCE_KEY[resourceKey];

    if (
      !HardAssetInspectionGate.isOfflineEnabledForAssetType(assetType) ||
      !UserPermissions.getPermission("inspectable_asset_types").includes(assetType)
    ) {
      return;
    }

    const resource = ResourceController.get(resourceKey);
    const download = _getDownload(resourceKey);
    download.queue = download.queue
      .then(() => change(resource, download))
      .catch((e) => Sentry.captureException(e));
  };

  const _toCachedRow = ({ assetInfo }) => ({ id: assetInfo.id, assetInfo });

  const _getDownload = (resourceKey) =>
    (downloads[resourceKey] ??= { downloadedGroupId: null, queue: Promise.resolve() });

  return { hasCacheForAssetType, fetchAll, startDownload, refreshAsset, removeAsset };
};

module.exports = InspectableAssetCache();

const ApiCalls = require("../apiCalls");
const ApiError = require("../errors/apiError");
const HardAssetInspectionGate = require("../muni/hardAssetInspectionGate");
const ResourceController = require("./resourceController");
const Sentry = require("@sentry/browser");
const Session = require("../login/session");
const UserPermissions = require("../login/userPermissions");
